import React, { useState, useEffect } from 'react';

const OverviewCards = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [userDetails, setUserDetails] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (category) => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/user-details/${category}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const users = await response.json();
        setUserDetails(users);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCardClick = async (cardIndex, category) => {
    if (expandedCard === cardIndex) {
      setExpandedCard(null);
      setUserDetails([]);
    } else {
      setExpandedCard(cardIndex);
      await fetchUserDetails(category);
    }
  };

  useEffect(() => {
    fetchData();
    const handleRefresh = () => {
      setLoading(true);
      fetchData();
    };
    window.addEventListener('analyticsRefresh', handleRefresh);
    return () => window.removeEventListener('analyticsRefresh', handleRefresh);
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: "Total Users",
      value: data?.totalUsers || 0,
      subtitle: `${data?.userBreakdown?.students || 0} students, ${data?.userBreakdown?.professors || 0} professors`,
      color: "blue",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      borderColor: "border-blue-200",
      category: "total"
    },
    {
      title: "Verified Users",
      value: data?.verifiedUsers || 0,
      subtitle: `${data?.verificationRate || 0}% verification rate`,
      color: "green",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      borderColor: "border-green-200",
      category: "verified"
    },
    {
      title: "Active Users",
      value: data?.activeUsers || 0,
      subtitle: "Recently logged in",
      color: "orange",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
      borderColor: "border-orange-200",
      category: "active"
    },
    {
      title: "Completed Profiles",
      value: data?.completedProfiles || 0,
      subtitle: `${data?.completionRate || 0}% completion rate`,
      color: "purple",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
      borderColor: "border-purple-200",
      category: "completed"
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <div 
            key={index} 
            className={`${card.bgColor} p-6 rounded-lg shadow border-l-4 ${card.borderColor} cursor-pointer hover:shadow-lg transition-shadow ${expandedCard === index ? 'ring-2 ring-blue-300' : ''}`}
            onClick={() => handleCardClick(index, card.category)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${card.textColor}`}>{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {card.value.toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">{card.subtitle}</p>
              </div>
              <div className={`text-xl ${card.textColor}`}>
                {expandedCard === index ? '▼' : '▶'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* User Details Section */}
      {expandedCard !== null && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">
            {cards[expandedCard].title} Details
          </h3>
          
          {loadingUsers ? (
            <div className="flex justify-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userDetails.map((user, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.role === 'student' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'professor' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLoginAt 
                          ? (() => {
                              const loginDate = new Date(user.lastLoginAt);
                              return `${loginDate.toLocaleDateString()} ${loginDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                            })()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewCards;